/**
 * Superuser allowlist (frontend copy).
 *
 * Defense-in-depth UI gate for the Audit Log viewer. The server-side pipeline
 * resolver (`auditAuthorizeFn.js`) is the AUTHORITATIVE check; this constant
 * only decides whether the navigation entry / viewer is shown, so the UI stays
 * uncluttered for non-superusers (Req 9.1, 9.2). A non-superuser who bypasses
 * the UI is still denied server-side.
 *
 * See design "Components and Interfaces > 2. Frontend — Audit Log Viewer" and
 * assumption A1.
 *
 * Requirements: 9.1, 9.2
 */

/**
 * The mirrored frontend copy of the server-authoritative Superuser_Allowlist.
 *
 * This is one of exactly two authoritative sources of truth for superuser
 * membership; the other is the resolver copy in
 * `SponsoredProductsSPFInfraCommon/.../resolvers/auditAuthorizeFn.js`.
 *
 * IMPORTANT: This list MUST stay in sync with the resolver copy. Adding a
 * future superuser is a one-line change here plus the same one-line change in
 * `auditAuthorizeFn.js` — no conditional-logic changes anywhere.
 */
export const SUPERUSER_IDS: number[] = [1, 8, 9, 46];

/**
 * Return true iff the given numeric user id is a member of SUPERUSER_IDS.
 *
 * Accepts null/undefined (an unresolved user_id) and returns false for them, so
 * callers can pass `userConfig?.id` directly. Membership uses
 * `Array.prototype.includes`, so extending the allowlist needs no logic change.
 */
export function isSuperuser(id: number | null | undefined): boolean {
  return id != null && SUPERUSER_IDS.includes(id);
}
